import { useCallback, useEffect, useState } from 'react'
import { ActionIcon, Badge, Box, Group, Select, TextInput } from '@mantine/core'
import { AppButton, AppStack, AppText } from '../../app/common/ui'
import { IoTrashOutline } from 'react-icons/io5'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import type { BuildCollaboratorDto } from '../../app/models/build'
import { useFeatureFlag } from '../../app/hooks/useFeatureFlag'

type Props = {
  buildId: string
  canManage: boolean
}

export default function BuildCollaboratorsSection ({ buildId, canManage }: Props) {
  const privacyOn = useFeatureFlag('builds-privacy-collaboration-enabled')
  const [collaborators, setCollaborators] = useState<BuildCollaboratorDto[]>([])
  const [loading, setLoading] = useState(false)
  const [inviteUsername, setInviteUsername] = useState('')
  const [inviteRole, setInviteRole] = useState<string>('Editor')
  const [inviting, setInviting] = useState(false)

  const loadCollaborators = useCallback(async () => {
    if (!privacyOn || !canManage || !buildId) return
    setLoading(true)
    try {
      setCollaborators(await agent.Builds.listCollaborators(buildId))
    } catch {
      setCollaborators([])
    } finally {
      setLoading(false)
    }
  }, [buildId, canManage, privacyOn])

  useEffect(() => {
    void loadCollaborators()
  }, [loadCollaborators])

  const handleInvite = async () => {
    const username = inviteUsername.trim()
    if (!username) return
    setInviting(true)
    try {
      await agent.Builds.inviteCollaborator(buildId, username, inviteRole)
      toast.success(`Invite sent to ${username}`)
      setInviteUsername('')
      await loadCollaborators()
    } catch {
      /* interceptor */
    } finally {
      setInviting(false)
    }
  }

  const handleRemove = async (collaboratorId: string) => {
    try {
      await agent.Builds.removeCollaborator(buildId, collaboratorId)
      toast.success('Collaborator removed')
      await loadCollaborators()
    } catch {
      /* interceptor */
    }
  }

  if (!privacyOn) {
    return (
      <>
        <AppText size="sm" color="dimmed">
          Collaborator invites activate when build privacy is enabled (Admin → Feature flags → Builds).
        </AppText>
        <Badge variant="outline" color="gray">Collaborators off</Badge>
      </>
    )
  }

  if (!canManage) {
    return (
      <AppText size="sm" color="dimmed">
        Only the build owner can manage collaborators.
      </AppText>
    )
  }

  return (
    <AppStack spacing="sm">
      <AppText size="sm" color="dimmed">
        Invite editors (can change parts on private builds) or viewers (read-only). They must accept the invite.
      </AppText>
      <Group align="flex-end" grow>
        <TextInput
          label="Username"
          placeholder="their-username"
          value={inviteUsername}
          onChange={(e) => setInviteUsername(e.currentTarget.value)}
        />
        <Select
          label="Role"
          data={[
            { value: 'Editor', label: 'Editor' },
            { value: 'Viewer', label: 'Viewer' }
          ]}
          value={inviteRole}
          onChange={(v) => setInviteRole(v ?? 'Editor')}
        />
      </Group>
      <AppButton
        size="sm"
        onClick={handleInvite}
        loading={inviting}
        disabled={!inviteUsername.trim()}
        sx={{ alignSelf: 'flex-start', background: 'var(--gs-accent)', '&:hover': { background: '#bf4a00' } }}
      >
        Send invite
      </AppButton>

      {loading
        ? <AppText size="sm" color="dimmed">Loading collaborators…</AppText>
        : collaborators.length === 0
          ? <Badge variant="outline" color="gray">No collaborators yet</Badge>
          : (
            <AppStack spacing="xs">
              {collaborators.map((c) => (
                <Group key={c.id} position="apart" noWrap sx={{
                  padding: 'var(--gs-sm) var(--gs-sm)',
                  borderRadius: 6,
                  border: '1px solid var(--gs-border)',
                  background: 'var(--gs-surface2)'
                }}>
                  <Box>
                    <AppText size="sm" weight={500}>@{c.username}</AppText>
                    <Group spacing={6} mt={4}>
                      <Badge size="xs" variant="outline">{c.role}</Badge>
                      {c.isPending && <Badge size="xs" color="yellow">Pending</Badge>}
                    </Group>
                  </Box>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    title={c.isPending ? 'Cancel invite' : 'Remove collaborator'}
                    onClick={() => void handleRemove(c.id)}
                  >
                    <IoTrashOutline size={16} />
                  </ActionIcon>
                </Group>
              ))}
            </AppStack>
            )}
    </AppStack>
  )
}
